import { ref } from 'vue'
import { useAnnouncementStore } from '@/store/announcement'
import { useLogger } from '@/composables/useLogger'

/**
 * Composable for handling announcement operations (CRUD)
 * Manages inline editing, creation, and deletion of announcements
 * Integrates with announcement store for state management and caching
 * 
 * Component handlers are responsible for triggering refresh after operations
 * 
 * @param {Array} announcements - The announcements array (passed as prop)
 * @returns {Object} - Edit state and functions
 */
export function useAnnouncementEdit(announcements) {
  const { sendLog } = useLogger()
  const announcementStore = useAnnouncementStore()
  
  // Track which announcements are in edit mode
  const editingStates = ref({})
  
  // Store temporary edit values
  const editValues = ref({})
  
  // Track loading state during save
  const savingStates = ref({})
  
  // Track loading state during delete
  const deletingStates = ref({})
  
  // Track errors
  const errors = ref({})
  
  // Track if creating new announcement
  const isCreatingNew = ref(false)
  
  // New announcement text
  const newAnnouncementText = ref('')
  
  // New announcement error
  const newAnnouncementError = ref(null)
  
  // Track if saving new announcement
  const isSavingNew = ref(false)

  /**
   * Start editing an announcement
   * @param {number} index - The index of the announcement in the array
   * @param {string} currentText - Current announcement text
   */
  const startEdit = (index, currentText) => {
    editingStates.value[index] = true
    editValues.value[index] = currentText
    errors.value[index] = null
  }

  /**
   * Cancel editing an announcement
   * @param {number} index - The index of the announcement in the array
   */
  const cancelEdit = (index) => {
    editingStates.value[index] = false
    editValues.value[index] = null
    errors.value[index] = null
  }

  /**
   * Check if an announcement is in edit mode
   * @param {number} index - The index of the announcement in the array
   * @returns {boolean} - Whether the announcement is being edited
   */
  const isEditing = (index) => {
    return editingStates.value[index] || false
  }

  /**
   * Check if an announcement is being saved
   * @param {number} index - The index of the announcement in the array
   * @returns {boolean} - Whether the announcement is being saved
   */
  const isSaving = (index) => {
    return savingStates.value[index] || false
  }

  /**
   * Check if an announcement is being deleted
   * @param {number} index - The index of the announcement in the array
   * @returns {boolean} - Whether the announcement is being deleted
   */
  const isDeleting = (index) => {
    return deletingStates.value[index] || false
  }

  /**
   * Get the edit value for an announcement
   * @param {number} index - The index of the announcement in the array
   * @returns {string} - The current edit value
   */
  const getEditValue = (index) => {
    return editValues.value[index] || ''
  }

  /**
   * Update the edit value for an announcement
   * @param {number} index - The index of the announcement in the array
   * @param {string} value - The new value
   */
  const updateEditValue = (index, value) => {
    editValues.value[index] = value
  }

  /**
   * Get error for an announcement
   * @param {number} index - The index of the announcement in the array
   * @returns {string|null} - The error message if any
   */
  const getError = (index) => {
    return errors.value[index] || null
  }

  /**
   * Save announcement changes
   * Returns success status - component handler will trigger refresh
   * 
   * @param {number} index - The index of the announcement in the array
   * @param {Object} announcement - The announcement object
   * @returns {Promise<boolean>} - Whether save was successful
   */
  const saveEdit = async (index, announcement) => {
    const newText = editValues.value[index]
    
    // Validation
    if (!newText || newText.trim()?.length === 0) {
      errors.value[index] = 'Announcement text cannot be empty'
      return false
    }

    // Defensive check - ensure announcement has an id
    if (!announcement.id && !announcement._id) {
      console.error('Update failed: Announcement object missing id field', announcement)
      errors.value[index] = 'Cannot update: Invalid announcement data. Please refresh the page.'
      return false
    }

    if (newText === announcement.text) {
      // No changes, just cancel edit mode
      cancelEdit(index)
      return true
    }

    savingStates.value[index] = true
    errors.value[index] = null

    try {
      // Use id if available, fallback to _id (for cached data from before mapping)
      const announcementId = announcement.id || announcement._id
      
      // Call store action which handles API call and cache update
      await announcementStore.updateExistingAnnouncement({
        id: announcementId,
        content: newText.trim()
      })

      // Exit edit mode
      editingStates.value[index] = false
      editValues.value[index] = null
      
      sendLog('info', 'Announcement updated successfully', { 
        announcementId: announcementId,
        index 
      })
      
      return true
    } catch (error) {
      errors.value[index] = error.message || 'Failed to update announcement. Please try again.'
      
      sendLog('error', 'Failed to update announcement', { 
        error: error.message,
        announcementId: announcement.id || announcement._id,
        index 
      })
      
      return false
    } finally {
      savingStates.value[index] = false
    }
  }

  /**
   * Delete an announcement
   * @param {number} index - The index of the announcement in the array
   * @param {Object} announcement - The announcement object
   * @returns {Promise<boolean>} - Whether delete was successful
   */
 const deleteAnnouncement = async (index, announcement) => {
  // Defensive check - ensure announcement has an id
  if (!announcement.id && !announcement._id) {
    errors.value[index] = 'Cannot delete: Invalid announcement data. Please refresh the page.'
    return false
  }

  if (!confirm('Are you sure you want to delete this announcement? This action cannot be undone.')) {
    return false
  }

  deletingStates.value[index] = true
  errors.value[index] = null

  try {
    // Use id if available, fallback to _id (for cached data from before mapping)
    const announcementId = announcement.id || announcement._id
    
    // Call store action which handles API call and cache invalidation
    await announcementStore.deleteExistingAnnouncement({
      id: announcementId
    })

    // Clean up edit state for this announcement
    cancelEdit(index) 
    
    sendLog('info', 'Announcement deleted successfully', { 
      announcementId: announcementId,
      index 
    })

    return true
  } catch (error) {
    console.error('Failed to delete announcement:', error)
    errors.value[index] = error.message || 'Failed to delete announcement. Please try again.'
    
    sendLog('error', 'Failed to delete announcement', { 
      error: error.message,
      announcementId: announcement.id || announcement._id,
      index 
    })
    
    return false
  } finally {
    deletingStates.value[index] = false
  }
}

  /**
   * Start creating a new announcement
   */
  const startNewAnnouncement = () => {
    isCreatingNew.value = true
    newAnnouncementText.value = ''
    newAnnouncementError.value = null
  }

  /**
   * Cancel creating a new announcement
   */
  const cancelNewAnnouncement = () => {
    isCreatingNew.value = false
    newAnnouncementText.value = ''
    newAnnouncementError.value = null
  }

  /**
   * Update new announcement text
   * @param {string} value - The new text value
   */
  const updateNewAnnouncementText = (value) => {
    newAnnouncementText.value = value
    // Clear error when user starts typing
    if (newAnnouncementError.value) {
      newAnnouncementError.value = null
    }
  }

  /**
   * Save new announcement
   * @returns {Promise<boolean>} - Whether save was successful
   */
  const saveNewAnnouncement = async () => {
    // Validation
    if (!newAnnouncementText.value || newAnnouncementText.value.trim()?.length === 0) {
      newAnnouncementError.value = 'Announcement text cannot be empty'
      return false
    }

    isSavingNew.value = true
    newAnnouncementError.value = null

    try {
      // Call store action which handles API call and cache invalidation
      const newAnnouncement = await announcementStore.createNewAnnouncement({
        text: newAnnouncementText.value.trim()
      })

      sendLog('info', 'Announcement created successfully', { 
        announcementId: newAnnouncement?.id
      })

      // Reset state
      isCreatingNew.value = false
      newAnnouncementText.value = ''
      
      return true
    } catch (error) {
      console.error('Failed to create announcement:', error)
      newAnnouncementError.value = error.message || 'Failed to create announcement. Please try again.'
      
      sendLog('error', 'Failed to create announcement', { 
        error: error.message
      })
      
      return false
    } finally {
      isSavingNew.value = false
    }
  }

  return {
    // Existing edit state
    editingStates,
    editValues,
    savingStates,
    deletingStates,
    errors,
    
    // New announcement state
    isCreatingNew,
    newAnnouncementText,
    newAnnouncementError,
    isSavingNew,
    
    // Existing functions
    startEdit,
    cancelEdit,
    isEditing,
    isSaving,
    isDeleting,
    getEditValue,
    updateEditValue,
    getError,
    saveEdit,
    
    // New functions
    deleteAnnouncement,
    startNewAnnouncement,
    cancelNewAnnouncement,
    updateNewAnnouncementText,
    saveNewAnnouncement
  }
}